import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button, ButtonGroup, Form, Dropdown, Alert } from "react-bootstrap";

const FILTERS = {
  TODAY: "today",
  WEEK: "this_week",
  SALARY: "salary_cycle",
  MONTH: "this_month",
  CUSTOM: "custom",
};

const FILTER_LABELS = {
  [FILTERS.TODAY]: "Day",
  [FILTERS.WEEK]: "Week",
  [FILTERS.SALARY]: "Half Month",
  [FILTERS.MONTH]: "Month",
  [FILTERS.CUSTOM]: "Custom",
};

const startOfDay = (d) => dayjs(d).startOf("day").toDate();
const endOfDay = (d) => dayjs(d).endOf("day").toDate();

export default function DateRangeFilter({
  filterType,
  startDate,
  endDate,
  onChange,
  maxDateCount = 31,
}) {
  const [error, setError] = useState(null);

  /* ---------------- Salary Cycle ---------------- */

  const getSalaryCycle = (base, step = 0) => {
    const baseDate = dayjs(base);

    const baseCycle = baseDate.date() <= 15 ? 0 : 1;
    const cycleIndex = baseCycle + step;

    const monthOffset = Math.floor(cycleIndex / 2);
    const cycleInMonth = cycleIndex % 2;

    const refMonth = baseDate.add(monthOffset, "month");

    if (cycleInMonth === 0) {
      return {
        start: refMonth.startOf("month").toDate(),
        end: refMonth.date(15).endOf("day").toDate(),
      };
    }

    return {
      start: refMonth.date(16).startOf("day").toDate(),
      end: refMonth.endOf("month").endOf("day").toDate(),
    };
  };

  useEffect(() => {
    selectFilter(filterType);
  }, []);

  /* ---------------- Range Builder ---------------- */

  const buildRange = (type, base = new Date(), step = 0) => {
    const ref = dayjs(base);

    switch (type) {
      case FILTERS.TODAY: {
        const d = ref.add(step, "day");
        return { start: startOfDay(d), end: endOfDay(d) };
      }

      case FILTERS.WEEK: {
        const d = ref.add(step, "week");
        return {
          start: d.startOf("week").toDate(),
          end: d.endOf("week").endOf("day").toDate(),
        };
      }

      case FILTERS.MONTH: {
        const d = ref.add(step, "month");
        return {
          start: d.startOf("month").toDate(),
          end: d.endOf("month").endOf("day").toDate(),
        };
      }

      case FILTERS.SALARY:
        return getSalaryCycle(ref, step);

      default:
        return null;
    }
  };

  /* ---------------- Navigation ---------------- */

  const navigate = (step) => {
    if (filterType === FILTERS.CUSTOM) return;

    const range = buildRange(filterType, startDate, step);
    if (!range) return;

    setError(null);
    onChange({ filterType, ...range });
  };

  /* ---------------- Filter Change ---------------- */

  const selectFilter = (type) => {
    let range = buildRange(type);

    if (type === FILTERS.CUSTOM) {
      range = { start: startDate, end: endDate };

      if (!startDate || !endDate) {
        range = buildRange(FILTERS.TODAY);
      }
    }

    setError(null);
    onChange({ filterType: type, ...range });
  };

  /* ---------------- Custom Range ---------------- */

  const updateCustom = (key, value) => {
    const next = {
      start: key === "start" ? dayjs(value).toDate() : startDate,
      end: key === "end" ? dayjs(value).toDate() : endDate,
    };

    const days =
      dayjs(next.end)
        .startOf("day")
        .diff(dayjs(next.start).startOf("day"), "day") + 1;

    if (maxDateCount && days > maxDateCount) {
      setError(`Custom date range cannot exceed ${maxDateCount} days.`);
      return;
    }

    setError(null);
    onChange({
      filterType: FILTERS.CUSTOM,
      start: startOfDay(next.start),
      end: endOfDay(next.end),
    });
  };

  /* ---------------- Display ---------------- */

  const rangeLabel = `${dayjs(startDate).format("DD MMM YYYY")} → ${dayjs(
    endDate
  ).format("DD MMM YYYY")}`;

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between gap-1">
        <div xs={3} className="text-right mb-2">
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary" size="sm">
              {FILTER_LABELS[filterType]}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {Object.values(FILTERS).map((f) => (
                <Dropdown.Item
                  key={f}
                  active={f === filterType}
                  onClick={() => selectFilter(f)}
                >
                  {FILTER_LABELS[f]}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="text-end mb-2">
          <ButtonGroup size="sm gap-2">
            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
              Prev
            </Button>
            <div className="fw-semibold small d-flex justify-content-between align-items-center">
              {rangeLabel}
            </div>
            <Button variant="outline-secondary" onClick={() => navigate(1)}>
              Next
            </Button>
          </ButtonGroup>
        </div>
      </div>

      {/* ===== Custom Date Inputs ===== */}
      {filterType === FILTERS.CUSTOM && (
        <div className="d-flex flex-column flex-sm-row gap-2 mb-2">
          <Form.Control
            type="date"
            value={dayjs(startDate).format("YYYY-MM-DD")}
            onChange={(e) => updateCustom("start", e.target.value)}
          />
          <Form.Control
            type="date"
            value={dayjs(endDate).format("YYYY-MM-DD")}
            onChange={(e) => updateCustom("end", e.target.value)}
          />
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
    </>
  );
}
